import type { RatingHistoryPoint } from './types';

/**
 * Utilitaires purs pour `<ProgressionStats>` (T5 P1 Stats Hub).
 *
 * Extraits dans un fichier dédié pour respecter
 * `react-refresh/only-export-components`.
 */

export type Trend = 'up' | 'down' | 'flat';

export interface ProgressionMetrics {
  delta30d: number | null;
  trend: Trend | null;
  recordValue: number | null;
  recordDate: string | null;
  pointsCount: number;
}

/**
 * Calcule les métriques de progression à partir d'une série historique brute.
 *
 *  - delta30d : (dernier point - premier point) DANS la fenêtre 30j (compte
 *    30j à rebours depuis le DERNIER point pour éviter les biais "pas joué
 *    depuis 6 mois").
 *  - trend : slope régression linéaire simple sur les points 30j.
 *    |slope| ≤ 0.05 ELO/jour (≈ 1.5 ELO sur 30j) → 'flat'. Sinon up/down.
 *  - record : max value sur toute la série + date associée.
 */
export function computeProgression(
  points: ReadonlyArray<RatingHistoryPoint>,
): ProgressionMetrics {
  if (points.length === 0) {
    return { delta30d: null, trend: null, recordValue: null, recordDate: null, pointsCount: 0 };
  }

  const sorted = [...points].sort((a, b) => a.rated_at.localeCompare(b.rated_at));

  let recordValue = sorted[0]!.value;
  let recordDate = sorted[0]!.rated_at;
  for (const p of sorted) {
    if (p.value > recordValue) {
      recordValue = p.value;
      recordDate = p.rated_at;
    }
  }

  const last = sorted[sorted.length - 1]!;
  const lastTs = Date.parse(last.rated_at + 'T00:00:00Z');
  const cutoff = lastTs - 30 * 86400_000;
  const window = sorted.filter((p) => Date.parse(p.rated_at + 'T00:00:00Z') >= cutoff);

  let delta30d: number | null = null;
  let trend: Trend | null = null;

  if (window.length >= 2) {
    const first = window[0]!;
    const lastInWin = window[window.length - 1]!;
    delta30d = lastInWin.value - first.value;

    const t0 = Date.parse(first.rated_at + 'T00:00:00Z');
    const xs: number[] = [];
    const ys: number[] = [];
    for (const p of window) {
      const days = (Date.parse(p.rated_at + 'T00:00:00Z') - t0) / 86400_000;
      xs.push(days);
      ys.push(p.value);
    }
    const n = xs.length;
    const sumX = xs.reduce((a, b) => a + b, 0);
    const sumY = ys.reduce((a, b) => a + b, 0);
    let sumXY = 0;
    let sumXX = 0;
    for (let i = 0; i < n; i++) {
      const x = xs[i]!;
      const y = ys[i]!;
      sumXY += x * y;
      sumXX += x * x;
    }
    const denom = n * sumXX - sumX * sumX;
    const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
    if (slope > 0.05) trend = 'up';
    else if (slope < -0.05) trend = 'down';
    else trend = 'flat';
  } else if (window.length === 1) {
    delta30d = 0;
    trend = 'flat';
  }

  return {
    delta30d,
    trend,
    recordValue,
    recordDate,
    pointsCount: sorted.length,
  };
}
